<template>
  <div
    class="rich-text-editor-wrapper"
    :class="{
      'inline': inline,
      'is-focused': isFocused
    }"
  >
    <div
      class="fixed-toolbar-container"
      :class="`fixed-toolbar-${tinymceKey}`"
    />
    <div
      v-if="!inline"
      class="block-wrapper"
    >
      <textarea
        :id="`block-textarea-${tinymceKey}`"
        class="block-textarea show-border"
        v-rich-text-editor
      />
      <span
        v-if="showPlaceholder"
        class="placeholder muted"
      >
        {{placeholder}}
      </span>
    </div>
    <div
      v-if="inline"
      class="inline-wrapper"
      :class="[
        alignmentClass,
        fontSizeClass,
      ]"
    >
      <div
        :id="`inline-textarea-${tinymceKey}`"
        class="inline-textarea show-border"
        :aria-label="ariaLabel"
        v-rich-text-editor
      ></div>
      <span
        v-if="showPlaceholder"
        class="placeholder muted"
        :class="{
          'is-hovered': hoverLabel && (inputLength || isFocused),
          'hoverable': hoverLabel,
        }"
      >
        {{placeholder}}
      </span>
    </div>
    <div class="v-text-field__details pt-1" >
      <div class="v-messages theme--light error--text">
        <div class="v-messages__wrapper">
          <div
            v-for="(error, errorIndex) in activeTinymceErrors"
            :key="errorIndex"
            class="v-messages__message py-1"
          >
            {{error.text}}
          </div>
        </div>
      </div>
      <div class="v-counter theme--light">{{charCount}}</div>
    </div>
    <AudioRecording
      v-if="recordMode === 'audio'"
      @upload-audio="uploadAudio"
      @exit-record-audio-mode="recordMode = null"
      @error="$emit('error', $event)"
      :is-processing-data.sync="localIsProcessingData"
    />
    <v-dialog
      content-class="full-height white overflow-y-hidden"
      :value="recordMode === 'video'"
      width="500"
      persistent
    >
      <playposit-video-transcoder
        v-if="recordMode === 'video'"
        :api-url="api.getServerUrl()"
        :token="api.getToken()"
        :teacher-id="1270877"
        :bulb-id="bulbId"
        starting-key="upload"
        @success="handleVideoTranscoderSuccess"
        @exit="handleVideoTranscoderExit"
      />
    </v-dialog>
  </div>
</template>

<script>
/* global tinymce */
import _ from 'lodash';
import api from '../../services/api';
import Interaction from '../../mixins/Interaction';
import AudioRecording from './AudioRecording.vue';

export default {
  name: 'RichTextEditor',
  components: {
    AudioRecording,
  },
  mixins: [
    Interaction,
  ],
  props: {
    alignmentClass: String,
    fontSizeClass: String,
    ariaLabel: String,
    errorText: String,
    existingText: String,
    placeholder: String,
    textModel: String,
    initialText: String,
    tinymceKey: [
      String,
      Number,
    ],
    bulbId: Number,
    hoverLabel: Boolean,
    inline: Boolean,
    enableUpload: Boolean,
    attachments: {
      type: Array,
      default: () => [],
    },
    disabledButtons: {
      type: Array,
      default: () => [],
    },
    teacher: {
      type: Object,
      required: true,
    },
    student: {
      type: Object,
      required: true,
    },
  },
  directives: {
    richTextEditor: {
      async bind(el, binding, vnode) {
        const v = vnode.context;
        const baseServerUrl = api.getServerUrl();
        const ua = window.navigator.userAgent;
        const iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i);
        const webkit = !!ua.match(/WebKit/i);
        const iOSSafari = iOS && webkit && !ua.match(/CriOS/i);
        await v.$nextTick();
        function initializeRichTextEditor() {
          tinymce.remove(`#${el.id}`);
          tinymce.init({
            custom: {
              node_api_prefix: baseServerUrl,
            },
            target: el,
            theme: 'modern',
            format: 'html',
            skin_url: 'https://cdnjs.cloudflare.com/ajax/libs/tinymce/4.7.6/skins/lightgray',
            min_height: 100,
            min_width: 250,
            body_class: 'inline-textarea',
            resize: true,
            entity_encoding: 'raw',
            relative_urls: false,
            keep_styles: false,
            forced_root_block: v.inline ? '' : 'p',
            inline: v.inline,
            fixed_toolbar_container: `.fixed-toolbar-${v.tinymceKey}`,
            link_assume_external_targets: true,
            menu: {
              edit: {
                title: 'Edit',
                items: 'undo redo  | cut copy paste selectall | searchreplace',
              },
              insert: {
                title: 'Insert',
                items: 'image link charmap eqneditor code',
              },
              format: {
                title: 'Format',
                items: 'underline bold italic | fontsizeselect formats | removeformat',
              },
              table: {
                title: 'Table',
                items: 'inserttable tableprops deletetable | cell row column',
              },
            },
            external_plugins: {
              eqneditor: `${baseServerUrl}hosted/eqneditor/plugin.min.js`,
              charcount: `${baseServerUrl}hosted/charcount/plugin.js`,
            },
            plugins: [
              'autoresize eqneditor advlist autolink lists link image charmap',
              'searchreplace visualblocks code',
              'table contextmenu paste autoresize imagetools',
            ],
            toolbar: iOSSafari ? 'fillBlank image eqneditor bold italic numlist bullist charmap showmenu' : 'fillBlank video wami image eqneditor bold italic numlist bullist charmap showmenu',
            style_formats: [
              {
                title: 'Headers',
                items: [
                  {
                    title: 'Header 1',
                    format: 'h1',
                  },
                  {
                    title: 'Header 2',
                    format: 'h2',
                  },
                  {
                    title: 'Header 3',
                    format: 'h3',
                  },
                ],
              },
              {
                title: 'Inline',
                items: [
                  {
                    title: 'Strikethrough',
                    icon: 'strikethrough',
                    format: 'strikethrough',
                  },
                  {
                    title: 'Superscript',
                    icon: 'superscript',
                    format: 'superscript',
                  },
                  {
                    title: 'Subscript',
                    icon: 'subscript',
                    format: 'subscript',
                  },
                  {
                    title: 'Code',
                    icon: 'code',
                    format: 'code',
                  },
                ],
              },
              {
                title: 'Alignment',
                items: [
                  {
                    title: 'Left',
                    icon: 'alignleft',
                    format: 'alignleft',
                  },
                  {
                    title: 'Center',
                    icon: 'aligncenter',
                    format: 'aligncenter',
                  },
                  {
                    title: 'Right',
                    icon: 'alignright',
                    format: 'alignright',
                  },
                  {
                    title: 'Justify',
                    icon: 'alignjustify',
                    format: 'alignjustify',
                  },
                ],
              },
            ],
            theme_advanced_font_sizes: '10px,12px,13px,14px,16px,18px,20px',
            font_size_style_values: '12px,13px,14px,16px,18px,20px',
            autosave_ask_before_unload: false,
            statusbar: false,
            images_upload_url: `${baseServerUrl}upload/image`,
            async images_upload_handler(blobInfo, success, failure) {
              try {
                if (v.readOnly) return failure('Images cannot be uploaded in read only mode.');
                await v.$timer(2000);
                const fd = new FormData();
                fd.append('file', blobInfo.blob(), blobInfo.filename());
                const { data: imageData } = await api.uploadImage(fd);
                return success(imageData.imageUrl);
              } catch(e) {
                return failure(e.message);
              }
            },
            image_class_list: [
              {
                title: 'Responsive',
                value: 'responsive',
              },
            ],
            setup: (ed) => {
              if (v.inline) {
                ed.addButton('showmenu', {
                  title: 'Show menu',
                  icon: 'more_vert',
                  onClick() {
                    const selector = `.fixed-toolbar-${v.tinymceKey} .mce-tinymce-inline.mce-container`;
                    const mainContainer = document.querySelector(selector);
                    if (!mainContainer) throw new Error(`Could not find "${selector}"`);
                    mainContainer.classList[mainContainer.classList.contains('extended') ? 'remove' : 'add']('extended');
                  },
                });
              }
              if (
                !v.disabledButtons
                || !v.disabledButtons.includes('wami')
              ) {
                ed.addButton('wami', {
                  title: 'Audio',
                  image: `${process.env.STATIC_DIR}img/mic.png`,
                  onClick() {
                    v.recordMode = (v.recordMode === 'audio') ? null : 'audio';
                    if (v.recordVode !== 'audio') return false;
                    v.scrollToElement('.audio-recorder');
                    return true;
                  },
                });
              }
              const hostingTeacherIdentity = _.get(v.rootTeacher, 'teacherIdentities', []).find((ti) => _.get(ti, 'identityType') === 'hosting');
              const hasHosting = (
                (
                  _.has(v.rootTeacher, 'school.hosting')
                  && v.rootTeacher.school.hosting === 1
                )
                || (
                  _.get(v.rootTeacher, 'featureLists', []).length
                  && parseInt(_.get(hostingTeacherIdentity, 'identityId', 0), 10)
                )
              );
              if (
                hasHosting
                && (
                  _.get(v, 'localInteraction.interactionType') !== 'free_response'
                  || _.get(v, 'localInteraction.config.enable_upload')
                )
              ) {
                ed.addButton('video', {
                  title: 'Video',
                  image: `${process.env.STATIC_DIR}img/videocam.png`,
                  onclick() {
                    v.recordMode = (v.recordMode === 'video') ? null : 'video';
                    if (
                      v.$getURLParameter('type') !== 'share'
                      || (
                        v.$getURLParameter('section_id')
                        && !v.user.isTeacherLmbAttempt
                      )
                    ) {
                      v.recordMode = (v.recordMode === 'video') ? null : 'video';
                    } else {
                      v.$emit('snackbar', {
                        text: 'Disabled in preview mode.',
                        color: 'warning',
                      });
                    }
                  },
                });
              }
              if (v.initialText) {
                ed.on('LoadContent', () => {
                  ed.setContent(v.initialText);
                });
              }
              ed.on('change', (e) => v.watchEditor(e, ed));
              ed.on('keyUp', (e) => v.watchEditor(e, ed));
              ed.on('focus', (e) => {
                document.querySelector(`.fixed-toolbar-${v.tinymceKey}`).classList.add('toolbar-open');
                v.isFocused = true;
                v.watchEditor(e, ed);
              });
              ed.on('blur', () => {
                v.isFocused = false;
                document.querySelector(`.fixed-toolbar-${v.tinymceKey}`).classList.remove('toolbar-open');
              });
              // [TODO] Figure out EventBus workaround...
              // EventBus.$on('setTinymce', (obj) => {
              //   if (obj.key !== v.tinymceKey) return;
              //   ed.setContent(obj.text);
              //   v.$emit('tinymce-input', obj.text);
              //   v.input_length = obj.text.length;
              // });
              // EventBus.$on('applyExistingText', (key) => {
              //   if (key !== v.tinymceKey) return;
              //   ed.setContent(v.existingText);
              //   v.$emit('tinymce-input', v.existingText);
              //   v.input_length = v.existingText.length || 0;
              //   v.charCount = ed.plugins.charcount.getCount(null, true); // count whitespace
              // });
              // EventBus.$on('focusTinymce', (key) => {
              //   if (key !== v.tinymceKey) return;
              //   ed.focus();
              // });
              // EventBus.$on('refreshTinymce', () => {
              //   tinymce.execCommand('mceRepaint');
              // });
              v.editor = ed;
              v.updateTinymceText(v.initialText);
            },
          });
        }
        initializeRichTextEditor();
      },
    },
  },
  data: () => ({
    editor: null,
    charCount: 0,
    isFocused: false,
    inputLength: 0,
    recordMode: null,
    tinymceErrors: [],
  }),
  computed: {
    localTextModel: {
      get() {
        return this.textModel || '';
      },
      set(textModel) {
        this.$emit('update:textModel', textModel);
      },
    },
    activeTinymceErrors() {
      return this.tinymceErrors.filter((error) => _.get(error, 'active'));
    },
    showPlaceholder() {
      return (
        this.hoverLabel
        || !this.inputLength
      );
    },
    api() {
      return api;
    },
  },
  methods: {
    async scrollToElement(selector) {
      const element = document.querySelector(selector);
      if (!element) return false;
      if (element.offsetTop === 0) {
        this.$timer(25);
        return this.scrollToElement(selector);
      }
      document.body.parentNode.scrollTop = element.offsetTop - 10; // <html> element
      return true;
    },
    watchEditor: _.throttle(function watchEditorThrottled(event, ed) {
      const content = ed.getContent();
      if (['<p><br data-mce-bogus="1"></p>', '<p><br></p>', ''].includes(content)) {
        ed.setContent('');
        this.localTextModel = '';
        this.inputLength = 0;
        // this.interactionMeta.isAnswerEmpty = true; // Define
      } else {
        this.localTextModel = content;
        this.inputLength = content.length;
        // this.interactionMeta.isAnswerEmpty = content.length === 0; // Define
      }
      this.charCount = ed.plugins.charcount.getCount(null, true); // count whitespace
    }, 250, {
      leading: true,
      trailing: true,
    }),
    updateTinymceText(text = '') {
      this.localTextModel = text;
      this.editor.setContent(text);
      this.$emit('tinymce-input', text);
      this.inputLength = text.length || 0;
      if (this.editor.plugins.charcount) {
        this.charCount = this.editor.plugins.charcount.getCount(null, true); // count whitespace
      }
    },
    async uploadAudio(recordedBlob) {
      try {
        const fd = new FormData();
        fd.append('file', recordedBlob);
        this.localIsProcessingData = true;
        const { data: audioData } = await api.uploadAudio(fd);
        if (!_.get(audioData, 'audioUrl')) {
          this.$emit('error', new Error('An error occurred while uploading the audio file.'));
        }
        this.editor.execCommand('mceInsertContent', false, `<p>&nbsp;</p><p><audio style="width: 200px" controls><source src="${audioData.audioUrl}" type="audio/wav"></audio></p><p>&nbsp;</p>`);
        this.editor.nodeChanged();
        this.recordMode = null;
        return audioData.audioUrl;
      } catch(e) {
        return this.$emit('error', e);
      } finally {
        this.localIsProcessingData = false;
      }
    },
    async handleVideoTranscoderSuccess(data) {
      await this.$timer(1000);
      this.recordMode = null;
      const [vWidth, vHeight] = _.get(data, 'aspectRatio', '16:9').split(':');
      const aspectPadding = parseFloat(((parseInt(vHeight, 10) / parseInt(vWidth, 10)) * 100).toFixed(2));
      let url = '/';
      if (this.$inDevEnvironment()) {
        url = this.$getServerUrl();
        if (url === '/') url = `${window.location.origin}/`;
      }
      const html = `
        <div class="mux-video-wrapper" style="position: relative; height: 0; padding-bottom: ${aspectPadding}%">
          <iframe class="clickable" src="${url}hosted/video/?video_id=${data.playpositVideo.id}" width="100%" height="100%" frameBorder="0" allowfullscreen style="position: absolute; width: 100%; height: 100%; top: 0; left: 0;"></iframe>
        </div>
      `;
      this.editor.execCommand('mceInsertContent', false, html);
      this.editor.nodeChanged();
    },
    handleVideoTranscoderExit(errorMessage) {
      this.recordMode = null;
      if (errorMessage) this.$emit('error', new Error(errorMessage));
    },
  },
  beforeDestroy() {
    tinymce.remove(`#${this.inline ? 'inline' : 'block'}-textarea-${this.tinymceKey}`);
  },
};
</script>

<style lang="scss">
  .rich-text-editor-wrapper {
    position: relative;
    max-width: 100%;

    &.inline {
      min-height: 38px;
    }

    &:not(.underline-only-on-focus) .inline-textarea.show-border {
      border-color: #9e9e9e;
    }

    &.underline-only-on-focus.is-focused .inline-textarea.show-border {
      border-color: #9e9e9e;
    }

    &.highlight-error .inline-textarea {
      border-color: #ff5252 !important;
    }

    .inline-wrapper,
    .block-wrapper {
      position: relative;
    }

    .inline-wrapper {
      &.align-with-radio {
        margin-top: 6px;

        .placeholder {
          top: 5px;
        }
      }

      &.align-with-checkbox {
        margin-top: 3px;
      }

      .placeholder {
        top: 5px;
        position: absolute;
        pointer-events: none;
        font-size: 14px;
        transition: all .1s linear;
        z-index: 2;
        width: 100%;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;

        &.hoverable {
          top: 3px;
          font-size: 16px;
        }

        &.is-hovered {
          top: -15px;
          font-size: 12px;
        }
      }
    }

    .block-wrapper .placeholder {
      position: absolute;
      top: 86px;
      left: 12px;
      pointer-events: none;
      font-size: 12px;
    }

    .fixed-toolbar-container {
      position: absolute;
      bottom: 100%;
      height: 69px;
      width: 100%;
      z-index: 10000;
      display: none;

      &.toolbar-open {
        display: block;
      }
    }

    .mce-tinymce {
      box-sizing: border-box;

      &:not(.mce-tinymce-inline) {
        min-height: 220px;
      }

      &.mce-container .mce-menubar {
        display: none;
      }
    }

    .mce-tinymce-inline.mce-container {
      height: 50% !important;
      position: absolute;
      top: auto !important;
      bottom: 0;

      &.extended {
        height: 100% !important;

        .mce-menubar {
          display: block;
        }
      }
    }

    .mce-container-body.mce-abs-layout {
      height: 100% !important;
      bottom: 0;
    }

    .mce-toolbar-grp.mce-last {
      top: auto !important;
      bottom: 0;
      left: -2px !important;

      >.mce-container-body {
        margin-top: 3px;
      }
    }

    .alert.warning > div {
      white-space: normal;
    }

    .inline-textarea {
      padding: 4px 0;
      border-bottom: 1px solid transparent;
      overflow-y: auto;
      overflow-x: hidden;
      cursor: text;
      max-height: 130px;
      min-height: 30px;
      line-height: 1.5;

      &.mce-edit-focus {
        outline: 0;
        border-color: var(--branding-active-hex);
      }
    }
  }

  i.mce-i-more_vert:before {
    content: "\e5d4";
    font-family: 'Material Icons';
    font-style: normal;
    font-weight: normal;
    text-decoration: inherit;
    color: #000;
    font-size: 1.5em;
    padding-right: 0.5em;
    position: absolute;
    top: 20%;
    left: 0;
  }
</style>